import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Tooltip,
  Switch
} from '@mui/material';
import apiService from '../../services/apiService';

/**
 * UserManagement component for admin user management
 * @returns {JSX.Element} UserManagement component
 */
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editRoleError, setEditRoleError] = useState('');
  const [editRoleSuccess, setEditRoleSuccess] = useState(false);
  const [editRoleLoading, setEditRoleLoading] = useState(false);
  const [patsDialogOpen, setPatsDialogOpen] = useState(false);
  const [patsAdoPat, setPatsAdoPat] = useState('');
  const [patsGithubPat, setPatsGithubPat] = useState('');
  const [patsAdoDirty, setPatsAdoDirty] = useState(false);
  const [patsGithubDirty, setPatsGithubDirty] = useState(false);
  const [patsLoading, setPatsLoading] = useState(false);
  const [patsError, setPatsError] = useState('');
  const [patsSuccess, setPatsSuccess] = useState(false);
  
  // Available roles
  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'dev_manager', label: 'Dev Manager' },
    { value: 'qa_manager', label: 'QA Manager' }
  ];

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.authGet('/api/auth/users');
      
      if (response.success) {
        setUsers(response.data);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Open reset password dialog
  const handleOpenResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetSuccess(false);
    setResetDialogOpen(true);
  };

  // Close reset password dialog
  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setSelectedUser(null);
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetSuccess(false);
  };
  
  // Open delete user dialog
  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteError('');
    setDeleteSuccess(false);
    setDeleteDialogOpen(true);
  };
  
  // Close delete user dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
    setDeleteError('');
    setDeleteSuccess(false);
  };
  
  // Open edit role dialog
  const handleOpenEditRoleDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role);
    setEditRoleError('');
    setEditRoleSuccess(false);
    setEditRoleDialogOpen(true);
  };
  
  // Close edit role dialog
  const handleCloseEditRoleDialog = () => {
    setEditRoleDialogOpen(false);
    setSelectedUser(null);
    setSelectedRole('');
    setEditRoleError('');
    setEditRoleSuccess(false);
  };

  const handleOpenPatsDialog = (user) => {
    setSelectedUser(user);
    setPatsAdoPat('');
    setPatsGithubPat('');
    setPatsAdoDirty(false);
    setPatsGithubDirty(false);
    setPatsError('');
    setPatsSuccess(false);
    setPatsDialogOpen(true);
  };

  const handleClosePatsDialog = () => {
    setPatsDialogOpen(false);
    setSelectedUser(null);
    setPatsAdoPat('');
    setPatsGithubPat('');
    setPatsAdoDirty(false);
    setPatsGithubDirty(false);
    setPatsError('');
    setPatsSuccess(false);
  };

  const handleSavePats = async () => {
    const body = {};
    if (patsAdoDirty) body.adoPat = patsAdoPat;
    if (patsGithubDirty) body.githubPat = patsGithubPat;
    if (Object.keys(body).length === 0) {
      setPatsError('Enter a value and click Save to update.');
      return;
    }
    try {
      setPatsLoading(true);
      setPatsError('');
      setPatsSuccess(false);
      const response = await apiService.authPut(`/api/auth/users/${selectedUser._id}/pats`, body);
      if (response.success) {
        setPatsSuccess(true);
        setPatsAdoPat('');
        setPatsGithubPat('');
        setPatsAdoDirty(false);
        setPatsGithubDirty(false);
        setTimeout(() => handleClosePatsDialog(), 2000);
      } else {
        setPatsError(response?.message || 'Failed to save PATs');
      }
    } catch (err) {
      setPatsError(err.message || 'Failed to save PATs');
    } finally {
      setPatsLoading(false);
    }
  };
  
  // Update user role
  const handleUpdateRole = async () => {
    try {
      setEditRoleLoading(true);
      setEditRoleError('');
      setEditRoleSuccess(false);
      
      // Validate role
      if (!selectedRole) {
        setEditRoleError('Please select a role');
        setEditRoleLoading(false);
        return;
      }
      
      // Call API to update user role
      const response = await apiService.authPut(`/api/auth/users/${selectedUser._id}/role`, {
        role: selectedRole
      });
      
      if (response.success) {
        setEditRoleSuccess(true);
        
        // Refresh user list
        fetchUsers();
        
        // Close dialog after 2 seconds
        setTimeout(() => {
          handleCloseEditRoleDialog();
        }, 2000);
      } else {
        setEditRoleError('Failed to update role: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating role:', err);
      setEditRoleError('Failed to update role: ' + (err.message || 'Unknown error'));
    } finally {
      setEditRoleLoading(false);
    }
  };
  
  // Delete user
  const handleDeleteUser = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError('');
      setDeleteSuccess(false);
      
      // Call API to delete user
      const response = await apiService.authDelete(`/api/auth/users/${selectedUser._id}`);
      
      if (response.success) {
        setDeleteSuccess(true);
        
        // Refresh user list
        fetchUsers();
        
        // Close dialog after 2 seconds
        setTimeout(() => {
          handleCloseDeleteDialog();
        }, 2000);
      } else {
        setDeleteError('Failed to delete user: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setDeleteError('Failed to delete user: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleGuardrailAccess = async (user) => {
    try {
      const response = await apiService.authPut(`/api/auth/users/${user._id}/guardrail-access`, {
        guardrailAccess: !user.guardrailAccess,
      });
      if (response.success) fetchUsers();
    } catch (err) {
      console.error('Error toggling guardrail access:', err);
    }
  };

  const handleToggleOperationalExcellenceAccess = async (user, granted) => {
    try {
      setError('');
      const response = await apiService.authPut(`/api/auth/users/${user._id}/operational-excellence-access`, {
        operationalExcellenceAccess: Boolean(granted),
      });
      if (response.success) {
        fetchUsers();
      } else {
        setError(response.message || 'Failed to update Operational Excellence access');
      }
    } catch (err) {
      console.error('Error toggling Operational Excellence access:', err);
      setError('Failed to update Operational Excellence access: ' + (err.message || 'Unknown error'));
    }
  };

  const handleToggleSurveyAdminAccess = async (user, granted) => {
    try {
      setError('');
      const response = await apiService.authPut(`/api/auth/users/${user._id}/survey-admin-access`, {
        surveyAdminAccess: Boolean(granted),
      });
      if (response.success) {
        fetchUsers();
      } else {
        setError(response.message || 'Failed to update survey admin access');
      }
    } catch (err) {
      console.error('Error toggling survey admin access:', err);
      setError('Failed to update survey admin access: ' + (err.message || 'Unknown error'));
    }
  };

  // Reset user password
  const handleResetPassword = async () => {
    try {
      setResetLoading(true);
      setResetError('');
      setResetSuccess(false);
      
      // Validate passwords
      if (!newPassword || !confirmPassword) {
        setResetError('Both fields are required');
        setResetLoading(false);
        return;
      }
      
      if (newPassword !== confirmPassword) {
        setResetError('Passwords do not match');
        setResetLoading(false);
        return;
      }
      
      if (newPassword.length < 6) {
        setResetError('Password must be at least 6 characters long');
        setResetLoading(false);
        return;
      }
      
      // Call API to reset password
      const response = await apiService.authPost(`/api/auth/reset-password/${selectedUser._id}`, {
        newPassword
      });
      
      if (response.success) {
        setResetSuccess(true);
        
        // Close dialog after 2 seconds
        setTimeout(() => {
          handleCloseResetDialog();
        }, 2000);
      } else {
        setResetError('Failed to reset password');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setResetError('Failed to reset password: ' + (err.message || 'Unknown error'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">User Management</Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={fetchUsers}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Guardrail</TableCell>
                <TableCell>Op. Excellence</TableCell>
                <TableCell>Survey admin</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.displayName}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role === 'superadmin' ? 'Super Admin' : user.role}
                        color={
                          user.role === 'superadmin' ? 'warning' :
                          user.role === 'admin' ? 'primary' :
                          user.role === 'engineer' ? 'default' : 
                          user.role === 'dev_manager' ? 'success' :
                          user.role === 'qa_manager' ? 'secondary' :
                          'default'
                        } 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={user.guardrailAccess ? 'Revoke Guardrail access' : 'Grant Guardrail access'}>
                        <Switch
                          size="small"
                          checked={!!user.guardrailAccess}
                          onChange={() => handleToggleGuardrailAccess(user)}
                          color="success"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={user.operationalExcellenceAccess ? 'Revoke Operational Excellence access' : 'Grant Operational Excellence access'}>
                        <Switch
                          size="small"
                          checked={!!user.operationalExcellenceAccess}
                          onChange={(event) => handleToggleOperationalExcellenceAccess(user, event.target.checked)}
                          color="success"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {user.role === 'admin' || user.role === 'superadmin' ? (
                        <Tooltip title="Admins can always manage surveys">
                          <span><Switch size="small" checked disabled color="success" /></span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={user.surveyAdminAccess ? 'Revoke survey admin access' : 'Let this user start, stop and reopen team surveys'}>
                          <Switch
                            size="small"
                            checked={!!user.surveyAdminAccess}
                            onChange={(event) => handleToggleSurveyAdminAccess(user, event.target.checked)}
                            color="success"
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {user.lastLogin 
                        ? new Date(user.lastLogin).toLocaleDateString() 
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Edit Role not shown for SuperAdmin (role is DB-only) */}
                        {user.role !== 'superadmin' && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenEditRoleDialog(user)}
                          >
                            Edit Role
                          </Button>
                        )}
                        {/* Reset/Delete/PATs for SuperAdmin users only available to SuperAdmins (API enforces) */}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenResetDialog(user)}
                        >
                          Reset Password
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenPatsDialog(user)}
                        >
                          Edit PATs
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleOpenDeleteDialog(user)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog}>
        <DialogTitle>
          Reset Password for {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a new password for this user. The password must be at least 6 characters long.
          </DialogContentText>
          
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}
          
          {resetSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password reset successfully!
            </Alert>
          )}
          
          <TextField
            label="New Password"
            type="password"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={resetLoading || resetSuccess}
            required
          />
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={resetLoading || resetSuccess}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} disabled={resetLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleResetPassword} 
            color="primary" 
            disabled={resetLoading || resetSuccess}
          >
            {resetLoading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialogOpen} onClose={handleCloseEditRoleDialog}>
        <DialogTitle>
          Edit Role for {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a new role for this user.
          </DialogContentText>
          
          {editRoleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editRoleError}
            </Alert>
          )}
          
          {editRoleSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Role updated successfully!
            </Alert>
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={selectedRole}
              label="Role"
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={editRoleLoading || editRoleSuccess}
            >
              {roles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditRoleDialog} disabled={editRoleLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateRole} 
            color="primary" 
            disabled={editRoleLoading || editRoleSuccess}
          >
            {editRoleLoading ? <CircularProgress size={24} /> : 'Update Role'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit PATs Dialog (admin or self) */}
      <Dialog open={patsDialogOpen} onClose={handleClosePatsDialog}>
        <DialogTitle>
          Edit PATs for {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set or clear ADO and GitHub Personal Access Tokens for this user. Leave blank to clear. Only the user or an admin can change PATs.
          </DialogContentText>
          {patsError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPatsError('')}>{patsError}</Alert>}
          {patsSuccess && <Alert severity="success" sx={{ mb: 2 }}>PATs updated successfully.</Alert>}
          <TextField
            label="ADO PAT"
            type="password"
            fullWidth
            margin="normal"
            value={patsAdoPat}
            onChange={(e) => { setPatsAdoPat(e.target.value); setPatsAdoDirty(true); }}
            placeholder="Enter to set or leave blank to clear"
            disabled={patsLoading || patsSuccess}
          />
          <TextField
            label="GitHub PAT"
            type="password"
            fullWidth
            margin="normal"
            value={patsGithubPat}
            onChange={(e) => { setPatsGithubPat(e.target.value); setPatsGithubDirty(true); }}
            placeholder="Enter to set or leave blank to clear"
            disabled={patsLoading || patsSuccess}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePatsDialog} disabled={patsLoading}>Cancel</Button>
          <Button onClick={handleSavePats} color="primary" disabled={patsLoading || patsSuccess}>
            {patsLoading ? <CircularProgress size={24} /> : 'Save PATs'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          Delete User: {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
          
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          
          {deleteSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              User deleted successfully!
            </Alert>
          )}
          
          {selectedUser && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Username:</Typography>
              <Typography variant="body2" gutterBottom>{selectedUser.username}</Typography>
              
              <Typography variant="subtitle2">Email:</Typography>
              <Typography variant="body2" gutterBottom>{selectedUser.email}</Typography>
              
              <Typography variant="subtitle2">Role:</Typography>
              <Typography variant="body2" gutterBottom>{selectedUser.role}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            disabled={deleteLoading || deleteSuccess}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserManagement;
